import { resolveService } from '@/api';
import type {
  ErrorMessageResponse,
  OrderBoxInput,
  ServicePickup,
  ServiceResolution,
} from '@/types';
import { multiBoxListOrNone } from '@/utils/orderBoxes';
import { useSentryQuery } from '../utils/sentryQueryWrapper';

/**
 * SVC-STEP increment 2 — the answer the wizard skips (or does not skip) on.
 *
 * ⚠️ THE DESTINATION IS PART OF THE KEY. The whole point of this endpoint is
 * that the answer changes per province, so a merchant who goes back and
 * changes the destination must NOT be skipped on the previous province's rule.
 * React Query keys on the province, so a change is a different question and a
 * stale answer can never be reused as if it were fresh.
 *
 * Silent (`defaultErrorMessage: ''`) for the same reason the catalogue query
 * is: every caller treats "no answer" as "show the step", so a transient 5xx
 * or a dashboard deployed ahead of the backend costs the merchant one extra
 * click, never a red toast and never a wrong skip.
 */
export const RESOLVE_SERVICE_QUERY_KEY = 'service-resolution';

const useResolveService = (
  provinceCode?: string | null,
  enabled = true,
  temperatureControl?: boolean,
  regionUid?: string | null,
  pickup?: ServicePickup,
  boxes?: OrderBoxInput[],
) =>
  useSentryQuery<ServiceResolution, ErrorMessageResponse>(
    // ⚠️ THE COLD FLAG IS PART OF THE KEY, for the same reason the province is:
    // the answer changes with it. A merchant who ticks Temperature Control on
    // an order already resolved warm must NOT be handed the warm answer — that
    // is precisely the case the owner reported (a Baghdad→Boxy Save rule and a
    // cold Baghdad order), and a shared key would skip the step on a stale yes.
    // BOXY-113 — and the REGION: a remote address can disable a service the
    // same province allows for an ordinary one.
    [
      RESOLVE_SERVICE_QUERY_KEY,
      provinceCode ?? '',
      temperatureControl ? 'cold' : 'warm',
      regionUid ?? '',
      // BOXY-530 — the pickup changes which services can be collected.
      pickup?.saved_pick_up_address_uid ?? '',
      pickup?.pick_up_address_uid ?? '',
      // BOXY-547 — the boxes change which services can take the order.
      (multiBoxListOrNone(boxes) ?? []).map((box) => box.size).join(','),
    ],
    () => resolveService(provinceCode, temperatureControl, regionUid, pickup, boxes),
    { enabled, defaultErrorMessage: '' },
  );

export default useResolveService;
